import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment";
import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import { Card } from "components/Dashboard/components";
import NoPermission from "components/Dashboard/components/NoPermission";
import { useGetFilterParams } from "components/Dashboard/hooks";
import { YYYY_MM_DD } from "shared/constants/dateFormats";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import { useApi } from "shared/hooks/useApi";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { usePermissions } from "shared/hooks/usePermissions";
import { RectangleLoader } from "shared/loaders/RectangleLoader";
import { ENDPOINTS, FILTER_NAMES } from "shared/types/enums";

import PeriodSettings from "./PeriodSettings";
import { ApiData, ImportedActivatedProps, TYPES } from "./types";

const ImportedActivated = ({ type }: ImportedActivatedProps) => {
    const { gray900 } = colors;

    const { t } = useTranslation();
    const { cancelSource, queryData, handleResponse, isCanceled } = useApi();
    const { stringifiedFilterParams } = useGetFilterParams();
    const { renderFormattedNumber } = useFormattedNumber();
    const { isReadDeviceNotAllowed } = usePermissions();
    const isMounted = useIsMounted();

    const [selectedPeriod, setSelectedPeriod] = useState(30);
    const [isLoading, setIsLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);

    const fields = {
        [TYPES.Imported]: FILTER_NAMES.ImportedDevices,
        [TYPES.Activated]: FILTER_NAMES.ActivatedDevices
    };

    useEffect(() => {
        const getTotalFromLastNDays = (data: ApiData, days: number) => {
            const startDate = moment
                .utc()
                .startOf("day")
                .subtract(days - 1, "days");

            const endDate = moment.utc().startOf("day");

            return Object.values(data).reduce((sum, { name, count }) => {
                const entryDate = moment.utc(name, YYYY_MM_DD);

                if (entryDate.isBetween(startDate, endDate, "day", "[]")) {
                    sum += Number(count);
                }

                return sum;
            }, 0);
        };

        const getStats = async () => {
            try {
                setIsLoading(true);

                const { data } = await queryData(
                    `${ENDPOINTS.DeviceStats}?field=${fields[type]}&limit=${selectedPeriod}`,
                    stringifiedFilterParams
                );

                if (isMounted) {
                    const total = getTotalFromLastNDays(data, selectedPeriod);
                    setTotalCount(total);
                    setIsLoading(false);
                }
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    handleResponse(error);
                    setTotalCount(0);
                    setIsLoading(false);
                }
            }
        };

        !isReadDeviceNotAllowed && getStats();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };
        // eslint-disable-next-line
    }, [isReadDeviceNotAllowed, stringifiedFilterParams, selectedPeriod]);

    const handleSelectedPeriod = (days: number) => setSelectedPeriod(days);

    return (
        <Card
            title={t(`Dashboard##title##${type}`)}
            infoTooltipText={
                !isReadDeviceNotAllowed
                    ? t(`Dashboard##tooltips##${type}`)
                    : undefined
            }
            containerCustomStyle={{
                display: "flex",
                flexDirection: "column",
                height: "100%"
            }}
            headerCustomStyle={{
                minHeight: isReadDeviceNotAllowed ? "40px" : "unset"
            }}
            bodyCustomStyle={{ flex: "1" }}
        >
            {isReadDeviceNotAllowed ? (
                <NoPermission
                    customStyle={{
                        "@media (max-width: 1439px)": {
                            marginBottom: "16px"
                        }
                    }}
                />
            ) : (
                <div
                    css={css({
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end"
                    })}
                >
                    <div
                        css={css({
                            color: gray900,
                            fontSize: "24px",
                            fontWeight: "500",
                            lineHeight: "32px",
                            padding: "6px 0 8px 0",
                            marginBottom: "38px",

                            "@media (max-width: 1439px)": {
                                marginBottom: "4px"
                            }
                        })}
                    >
                        {isLoading ? (
                            <RectangleLoader
                                width={100}
                                height={24}
                                customStyle={{ margin: "4px 0" }}
                            />
                        ) : (
                            renderFormattedNumber(totalCount)
                        )}
                    </div>

                    <PeriodSettings
                        period={selectedPeriod}
                        onChange={handleSelectedPeriod}
                    />
                </div>
            )}
        </Card>
    );
};

export default ImportedActivated;
export { TYPES as IMPORTED_ACTIVATED_TYPES } from "./types";
